import { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { useSignOut, useSignIn } from "react-auth-kit";
import { sendApiRequest } from "../../services/Base/base-service";

import UserContext from "../../store/user-context";

import TableSearch from "./TableSearch";
import TableHeader from "./TableHeader";
import ExternalFlowStatusEnum from "../../helpers/Enums/ExternalFlowStatusEnum";
import DownloadTypeEnum from "../../helpers/Enums/DownloadTypeEnum";
import ConfirmDeleteModal from "../Modals/ConfirmDeleteModal";
import ProgressBar from "../ProgressBar";
import {
  Table,
  Icon,
  Alert,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  NotificationManager,
  Chip,
} from "design-react-kit";

import "../../assets/Table.css";
import ExternalFlowErrorsModal from "../Modals/ExternalFlowErrorsModal";
import { closeDropdown } from "../../services/helpers";

const ExternalFlowsTable = ({
  items,
  filteredItems,
  columns,
  uploadFlowModalOpenHandler,
  uploadFlowSignModalOpenHandler,
  confirmSendModalOpenHandler,
  authorizeForSending,
  getExternalFlows,
  searchQuery,
  setSearchQuery,
}) => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [deleteFlowModalOpen, setDeleteFlowModalOpen] = useState(false);
  const [deleteSignedZipModalOpen, setDeleteSignedZipModalOpen] =
    useState(false);
  const [extFlowErrorsModalOpen, setExtFlowErrorsModalOpen] = useState(false);
  const [fileToDownload, setFileToDownload] = useState(null);

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  useEffect(() => {
    if (filteredItems !== null) {
      setResults(filteredItems);
    } else {
      setResults(items);
    }
  }, [items, filteredItems]);

  const showExtFlowStatus = (flow) => {
    switch (Number(flow.statusId)) {
      case ExternalFlowStatusEnum.Initial:
        return (
          <Chip color="white" className="text-nowrap">
            {flow.statusLabel}
          </Chip>
        );
      case ExternalFlowStatusEnum.ContentValidation:
      case ExternalFlowStatusEnum.SignValidation:
        return (
          <Chip color="info" className="text-nowrap">
            {flow.statusLabel}
          </Chip>
        );
      case ExternalFlowStatusEnum.ContentValid:
      case ExternalFlowStatusEnum.ReadyToSend:
      case ExternalFlowStatusEnum.SignValid:
      case ExternalFlowStatusEnum.Sent:
        return (
          <Chip color="success" className="text-nowrap">
            {flow.statusLabel}
          </Chip>
        );
      case ExternalFlowStatusEnum.ContentNotValid:
      case ExternalFlowStatusEnum.SignNotValid:
      case ExternalFlowStatusEnum.NotSent:
        return (
          <span
            style={{ cursor: "pointer" }}
            title="Clicca qui per vedere il file con gli errori"
            onClick={(e) => extFlowErrorsModalOpenHandler(flow)}
          >
            <Chip color="danger" className="text-nowrap" role="button">
              {flow.statusLabel}
            </Chip>
          </span>
        );
      default:
        return (
          <Chip color="white" className="text-nowrap">
            {flow.statusLabel}
          </Chip>
        );
    }
  };

  const deleteFlow = (flowId) => {
    setIsLoading(true);
    sendApiRequest(
      `ExternalFlow/DeleteExternalFlow/${flowId}`,
      "DELETE",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined && response.errors !== null) {
        setError(response.errors);
      } else {
        getExternalFlows();
      }
      setIsLoading(false);
    });
  };

  const deleteFlowZipSigned = (flowId) => {
    setIsLoading(true);
    sendApiRequest(
      `ExternalFlow/DeleteZipWithSignature/${flowId}`,
      "DELETE",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined && response.errors !== null) {
        setError(response.errors);
      } else {
        getExternalFlows();
      }
      setIsLoading(false);
    });
  };

  const authorizeForSendingHandler = (flow) => {
    if (flow.statusId === ExternalFlowStatusEnum.ContentValid) {
      confirmSendModalOpenHandler(flow);
    }
    if (flow.statusId === ExternalFlowStatusEnum.SignValid) {
      authorizeForSending(flow.id);
    }
  };

  const filterHandler = (e, items) => {
    const value = e.target.value;
    let filteredResults = "";
    if (value === "") {
      filteredResults = items;
    } else if (
      e.nativeEvent.inputType === "deleteContentBackward" ||
      e.nativeEvent.inputType === "deleteContentForward"
    ) {
      filteredResults = items.filter((item) => {
        return (
          item.zipFileName.toLowerCase().includes(value.toLowerCase()) ||
          item.clientCode.toLowerCase().includes(value.toLowerCase())
        );
      });
    } else {
      filteredResults = items.filter((item) => {
        return (
          item.zipFileName.toLowerCase().includes(value.toLowerCase()) ||
          item.clientCode.toLowerCase().includes(value.toLowerCase())
        );
      });
    }

    setSearchQuery(value);
    return filteredResults;
  };

  const deleteFlowModalOpenHandler = (flow) => {
    closeDropdown();
    setDeleteFlowModalOpen(true);
    setSelectedFlow(flow);
  };

  const deleteFlowModalCloseHandler = () => {
    setDeleteFlowModalOpen(false);
    setSelectedFlow(null);
    getExternalFlows();
  };

  const deleteSignedZipModalOpenHandler = (flow) => {
    closeDropdown();
    setDeleteSignedZipModalOpen(true);
    setSelectedFlow(flow);
  };

  const extFlowErrorsModalOpenHandler = (flow) => {
    closeDropdown();
    setExtFlowErrorsModalOpen(true);
    setSelectedFlow(flow);
  };

  const deleteSignedZipModalCloseHandler = () => {
    setDeleteSignedZipModalOpen(false);
    setSelectedFlow(null);
    getExternalFlows();
  };

  const extFlowErrorsModalCloseHandler = () => {
    setExtFlowErrorsModalOpen(false);
    setSelectedFlow(null);
    getExternalFlows();
  };

  return (
    <Fragment>
      <div className="d-flex flex-column align-items-end justify-content-start">
        <div className="d-flex align-items-start justify-content-end w-100">
          <TableSearch
            items={items}
            filterHandler={filterHandler}
            setResults={setResults}
            text={"Cerca"}
            value={searchQuery}
          />
          <span title="Aggiornare i dati della tabella">
            <Icon
              icon="it-refresh"
              fill="#06c"
              className="ml-3 mt-1 mb-2"
              type="button"
              onClick={(e) => getExternalFlows(searchQuery)}
            />
          </span>
        </div>
        {error !== null && error.length !== undefined && (
          <Alert color="danger" className="mx-auto">
            {error.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </Alert>
        )}

        <Table responsive className="table">
          <TableHeader
            columns={columns}
            setResults={setResults}
            items={items}
          />
          <tbody>
            {results !== null &&
              results !== undefined &&
              results.map((result) => {
                return (
                  <tr key={result.id}>
                    <td className="zipUploadDate text-center">
                      {result.zipUploadDate}
                    </td>
                    <td className="zipFileName text-center ">
                      <ProgressBar
                        result={result}
                        setError={setError}
                        type={DownloadTypeEnum.ExternalFlowZip}
                        fileToDownload={fileToDownload}
                      />
                    </td>
                    <td className="zipWithSignatureFileName text-center">
                      <ProgressBar
                        result={result}
                        setError={setError}
                        type={DownloadTypeEnum.ExternalFlowZipWithSign}
                        fileToDownload={fileToDownload}
                      />
                    </td>
                    <td className="clientCode text-center">
                      {result.clientCode}
                    </td>
                    <td className="text-center">{showExtFlowStatus(result)}</td>
                    <td className="text-center externalFlowsDropdown">
                      {result.statusId !==
                        ExternalFlowStatusEnum.ContentValidation &&
                        result.statusId !==
                          ExternalFlowStatusEnum.SignValidation &&
                        result.statusId !==
                          ExternalFlowStatusEnum.ReadyToSend && (
                          <UncontrolledDropdown className="table-dropdown text-nowrap">
                            <DropdownToggle>
                              <Icon icon="it-more-actions" fill="#06c" />
                              <Link to={null} className="moreActions">
                                Vedi azioni
                              </Link>
                            </DropdownToggle>
                            <DropdownMenu>
                              {(result.statusId ===
                                ExternalFlowStatusEnum.Initial ||
                                result.statusId ===
                                  ExternalFlowStatusEnum.ContentValid ||
                                result.statusId ===
                                  ExternalFlowStatusEnum.ContentNotValid) && (
                                <div className="d-flex align-items-center ml-3">
                                  <Icon icon="it-upload" fill="#06c" />
                                  <Link
                                    to={null}
                                    className="uploadFlowZip"
                                    onClick={(e) =>
                                      uploadFlowModalOpenHandler(result)
                                    }
                                  >
                                    Carica flusso notifiche
                                  </Link>
                                </div>
                              )}
                              <div className="d-flex align-items-center ml-3">
                                <Icon icon="it-download" fill="#06c" />
                                <Link
                                  to={null}
                                  className="downloadFlowZip mr-4"
                                  onClick={(e) =>
                                    setFileToDownload(result.zipFileName)
                                  }
                                >
                                  Scarica flusso notifiche
                                </Link>
                              </div>
                              {result.statusId !==
                                ExternalFlowStatusEnum.ContentValidation &&
                                result.statusId !==
                                  ExternalFlowStatusEnum.SignValidation &&
                                result.statusId !==
                                  ExternalFlowStatusEnum.ReadyToSend && (
                                  <>
                                    <div className="d-flex align-items-center ml-3">
                                      <Icon icon="it-delete" fill="#06c" />
                                      <Link
                                        to={null}
                                        className="removeFlowZip"
                                        onClick={(e) =>
                                          deleteFlowModalOpenHandler(result)
                                        }
                                      >
                                        Rimuovi flusso notifiche
                                      </Link>
                                    </div>
                                  </>
                                )}
                              {(result.statusId ===
                                ExternalFlowStatusEnum.ContentValid ||
                                result.statusId ===
                                  ExternalFlowStatusEnum.SignNotValid) && (
                                <>
                                  <hr />
                                  <ProgressBar
                                    result={result}
                                    setError={setError}
                                    type={
                                      DownloadTypeEnum.ExternalFlowZipForSign
                                    }
                                  />
                                </>
                              )}
                              {(result.statusId ===
                                ExternalFlowStatusEnum.ContentNotValid ||
                                result.statusId ===
                                  ExternalFlowStatusEnum.SignNotValid ||
                                result.statusId ===
                                  ExternalFlowStatusEnum.NotSent) && (
                                <>
                                  <hr />
                                  <div className="d-flex align-items-center ml-3">
                                    <Icon icon="it-error" fill="#06c" />
                                    <Link
                                      to={null}
                                      className="ErrorDetails"
                                      onClick={(e) =>
                                        extFlowErrorsModalOpenHandler(result)
                                      }
                                    >
                                      Dettaglio errori
                                    </Link>
                                  </div>
                                </>
                              )}
                              <hr />
                              {(result.statusId ===
                                ExternalFlowStatusEnum.ContentValid ||
                                result.statusId ===
                                  ExternalFlowStatusEnum.SignNotValid) && (
                                <div className="d-flex align-items-center ml-3">
                                  <Icon icon="it-upload" fill="#06c" />
                                  <Link
                                    to={null}
                                    className="uploadZipWithSignedFiles"
                                    onClick={(e) =>
                                      uploadFlowSignModalOpenHandler(result)
                                    }
                                  >
                                    Carica PDF firmati
                                  </Link>
                                </div>
                              )}
                              {result.zipWithSignatureFileName !== null && (
                                <div className="d-flex align-items-center ml-3">
                                  <Icon icon="it-download" fill="#06c" />
                                  <Link
                                    to={null}
                                    className="downloadZipWithSignedFiles"
                                    onClick={(e) =>
                                      setFileToDownload(
                                        result.zipWithSignatureFileName
                                      )
                                    }
                                  >
                                    Scarica PDF firmati
                                  </Link>
                                </div>
                              )}
                              {result.zipWithSignatureFileName !== null &&
                                result.statusId ===
                                  ExternalFlowStatusEnum.SignNotValid && (
                                  <div className="d-flex align-items-center ml-3">
                                    <Icon icon="it-delete" fill="#06c" />
                                    <Link
                                      to={null}
                                      className="removeZipWithSignedFiles"
                                      onClick={(e) =>
                                        deleteSignedZipModalOpenHandler(result)
                                      }
                                    >
                                      Rimuovi PDF firmati
                                    </Link>
                                  </div>
                                )}
                              {result.statusId ===
                                ExternalFlowStatusEnum.ContentValid && (
                                <>
                                  <hr />
                                  <div className="d-flex align-items-center ml-3">
                                    <Icon icon="it-mail" fill="#06c" />
                                    <Link
                                      to={null}
                                      className="sendFlow"
                                      onClick={(e) =>
                                        authorizeForSendingHandler(result)
                                      }
                                    >
                                      Trasmetti flusso notifiche
                                    </Link>
                                  </div>
                                </>
                              )}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        )}
                    </td>
                  </tr>
                );
              })}
            {results !== null && results.length === 0 && (
              <tr>
                <td colSpan={12}>
                  <Alert className="w-50 mx-auto" color="info">
                    Nessun risultato trovato!
                  </Alert>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <ConfirmDeleteModal
        confirmModalOpen={deleteFlowModalOpen}
        confirmModalCloseHandler={deleteFlowModalCloseHandler}
        flowId={selectedFlow?.id}
        target={selectedFlow?.zipFileName}
        error={error}
        isLoading={isLoading}
        text={"Stai per eliminare il flusso notifiche"}
        actionHandler={deleteFlow}
        confirmButtonText="Sì, elimina"
      />
      <ConfirmDeleteModal
        confirmModalOpen={deleteSignedZipModalOpen}
        confirmModalCloseHandler={deleteSignedZipModalCloseHandler}
        flowId={selectedFlow?.id}
        target={selectedFlow?.zipWithSignatureFileName}
        error={error}
        isLoading={isLoading}
        text={"Stai per eliminare il file con i PDF firmati"}
        actionHandler={deleteFlowZipSigned}
        confirmButtonText="Sì, elimina"
      />
      <ExternalFlowErrorsModal
        modalOpen={extFlowErrorsModalOpen}
        modalCloseHandler={extFlowErrorsModalCloseHandler}
        selectedFlow={selectedFlow}
      />
      <div>
        <NotificationManager
          containerId="table-user-message"
          fix="right"
          closeOnClick={true}
          state="success"
          duration={30000}
        />
      </div>
    </Fragment>
  );
};

export default ExternalFlowsTable;
